/**
 * Gestionnaire centralisé des listeners Firestore en temps réel
 *
 * Ce service résout le problème CORS qui survient lors du refresh du token Azure :
 * - Les listeners Firestore (onSnapshot) restent actifs avec l'ancienne session d'auth
 * - Lors du refresh, ils tentent d'utiliser une session expirée
 * - Cela provoque une erreur CORS sur l'API Firestore Listen channel
 *
 * Solution : Fermer tous les listeners avant le refresh et les redémarrer après
 */

class FirestoreListenerManager {
  constructor() {
    // Registre des listeners actifs : { id: { unsubscribe, restart } }
    this.listeners = new Map();

    // Exposer globalement pour l'accès depuis AuthContext
    if (typeof window !== 'undefined') {
      window.__firestoreListenerManager = this;
    }
  }

  /**
   * Enregistre un listener Firestore
   * @param {string} id - Identifiant unique du listener
   * @param {Function} unsubscribeFn - Fonction de désabonnement retournée par onSnapshot
   * @param {Function} restartFn - Fonction optionnelle pour redémarrer le listener
   */
  registerListener(id, unsubscribeFn, restartFn = null) {
    if (!id || typeof unsubscribeFn !== 'function') {
      console.warn('[ListenerManager] ⚠️ Impossible d\'enregistrer le listener:', id);
      return;
    }

    this.listeners.set(id, {
      unsubscribe: unsubscribeFn,
      restart: restartFn,
      registeredAt: Date.now()
    });
  }

  /**
   * Désenregistre un listener
   * @param {string} id - Identifiant du listener à désenregistrer
   */
  unregisterListener(id) {
    const listener = this.listeners.get(id);
    if (listener) {
      // Appeler unsubscribe si le listener existe toujours
      try {
        if (listener.unsubscribe) {
          listener.unsubscribe();
        }
      } catch (error) {
        console.warn('[ListenerManager] ⚠️ Erreur lors du désabonnement:', error);
      }
      this.listeners.delete(id);
    }
  }

  /**
   * Ferme tous les listeners actifs
   * Appelé AVANT le refresh du token Azure
   */
  closeAllListeners() {
    const count = this.listeners.size;

    this.listeners.forEach((listener, id) => {
      try {
        if (listener.unsubscribe) {
          listener.unsubscribe();
        }
      } catch (error) {
        console.warn(`[ListenerManager] ⚠️ Erreur fermeture listener ${id}:`, error);
      }
    });

    // Ne pas vider le registre, on garde les infos pour le restart
  }

  /**
   * Redémarre tous les listeners
   * Appelé APRÈS le refresh du token Azure et le nouveau signIn
   */
  restartAllListeners() {
    const listenersToRestart = Array.from(this.listeners.entries());

    listenersToRestart.forEach(([id, listener]) => {
      if (listener.restart && typeof listener.restart === 'function') {
        try {
          // Appeler la fonction de redémarrage
          const newUnsubscribe = listener.restart();

          // Mettre à jour avec la nouvelle fonction d'unsubscribe
          if (newUnsubscribe && typeof newUnsubscribe === 'function') {
            this.listeners.set(id, {
              ...listener,
              unsubscribe: newUnsubscribe,
              registeredAt: Date.now()
            });
          }
        } catch (error) {
          console.warn(`[ListenerManager] ⚠️ Erreur redémarrage listener ${id}:`, error);
        }
      }
    });
  }

  /**
   * Nettoie complètement tous les listeners
   * Utilisé lors de la déconnexion
   */
  clearAll() {
    this.closeAllListeners();
    this.listeners.clear();
  }

  /**
   * Retourne le nombre de listeners actifs
   */
  getActiveListenersCount() {
    return this.listeners.size;
  }

  /**
   * Retourne la liste des IDs de listeners actifs (pour debug)
   */
  getActiveListenerIds() {
    return Array.from(this.listeners.keys());
  }
}

// Instance singleton
const firestoreListenerManager = new FirestoreListenerManager();

export default firestoreListenerManager;
